import { useState, useRef } from "react";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { Building2, Upload, Loader2, Phone, FileText, LogOut, ArrowLeft, CheckCircle2 } from "lucide-react";
import { useAuth } from "../context/AuthContext";
import { useLang } from "../i18n/LanguageContext";
import { api, fileUrl } from "../lib/api";
import { Logo } from "../components/Logo";
import { LanguageSwitcher } from "../components/LanguageSwitcher";
import { Input } from "../components/ui/input";
import { Button } from "../components/ui/button";

const TXT = {
  title: { ar: "أكمل بيانات شركتك للمتابعة", en: "Complete your company profile to continue", ur: "جاری رکھنے کے لیے کمپنی کی معلومات مکمل کریں" },
  subtitle: {
    ar: "قبل الدخول للوحة التحكم، نحتاج بعض البيانات الأساسية عن شركتك. هذه الخطوة تُعبّأ مرة واحدة فقط.",
    en: "Before entering the dashboard, we need a few essential details about your company. This is a one-time step.",
    ur: "ڈیش بورڈ میں داخل ہونے سے پہلے، ہمیں آپ کی کمپنی کے بارے میں چند بنیادی تفصیلات درکار ہیں۔",
  },
  logo: { ar: "شعار الشركة (اختياري)", en: "Company logo (optional)", ur: "کمپنی کا لوگو (اختیاری)" },
  logoBtn: { ar: "رفع الشعار", en: "Upload logo", ur: "لوگو اپلوڈ کریں" },
  nameL: { ar: "اسم الشركة", en: "Company name", ur: "کمپنی کا نام" },
  nameP: { ar: "مثال: شركة النخبة للتوصيل", en: "e.g. Elite Delivery Co.", ur: "مثلاً: ایلیٹ ڈیلیوری کمپنی" },
  phoneL: { ar: "رقم الجوال", en: "Mobile number", ur: "موبائل نمبر" },
  phoneP: { ar: "05xxxxxxxx", en: "05xxxxxxxx", ur: "05xxxxxxxx" },
  crL: { ar: "السجل التجاري", en: "Commercial registration (CR)", ur: "کمرشل رجسٹریشن" },
  crP: { ar: "رقم السجل التجاري", en: "CR number", ur: "CR نمبر" },
  required: { ar: "إلزامي", en: "Required", ur: "لازمی" },
  submit: { ar: "حفظ ومتابعة", en: "Save & continue", ur: "محفوظ کریں اور جاری رکھیں" },
  fillAll: { ar: "الرجاء تعبئة اسم الشركة ورقم الجوال والسجل التجاري", en: "Please fill company name, mobile number and CR", ur: "براہ کرم تمام لازمی خانے بھریں" },
  saved: { ar: "تم حفظ بيانات الشركة ✓", en: "Company details saved ✓", ur: "کمپنی کی معلومات محفوظ ہو گئیں ✓" },
  saveFail: { ar: "تعذّر الحفظ، حاول مرة أخرى", en: "Could not save, please try again", ur: "محفوظ نہ ہو سکا" },
  logoFail: { ar: "تعذّر رفع الشعار", en: "Could not upload logo", ur: "لوگو اپلوڈ نہ ہو سکا" },
  logout: { ar: "تسجيل الخروج", en: "Log out", ur: "لاگ آؤٹ" },
};

export default function Onboarding() {
  const { refreshSubscription, logout } = useAuth();
  const { dir, lang } = useLang();
  const tx = (k) => TXT[k][lang] || TXT[k].ar;
  const [name, setName] = useState("");
  const [phone, setPhone] = useState("");
  const [cr, setCr] = useState("");
  const [logoId, setLogoId] = useState(null);
  const [logoBusy, setLogoBusy] = useState(false);
  const [saving, setSaving] = useState(false);
  const logoRef = useRef(null);

  const onLogo = async (e) => {
    const file = e.target.files?.[0]; e.target.value = "";
    if (!file) return;
    setLogoBusy(true);
    try {
      const res = await api.uploadCompanyLogo(file);
      setLogoId(res.logo_id);
    } catch (err) {
      toast.error(err?.response?.data?.detail || tx("logoFail"));
    } finally { setLogoBusy(false); }
  };

  const submit = async () => {
    if (!name.trim() || !phone.trim() || !cr.trim()) {
      toast.warning(tx("fillAll"));
      return;
    }
    setSaving(true);
    try {
      await api.updateCompany({ name: name.trim(), phone: phone.trim(), cr_number: cr.trim() });
      toast.success(tx("saved"));
      await refreshSubscription();
    } catch (e) {
      toast.error(e?.response?.data?.detail || tx("saveFail"));
    } finally { setSaving(false); }
  };

  return (
    <div dir={dir} className="min-h-screen bg-gradient-to-b from-blue-900 via-slate-900 to-slate-900 py-10 px-4"
      data-testid="onboarding-screen">
      <div className="mx-auto max-w-xl">
        <div className="flex justify-end mb-4"><LanguageSwitcher tone="dark" /></div>
        <div className="flex justify-center mb-6"><Logo size="lg" textClass="text-white" /></div>

        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.45 }}
          className="rounded-3xl bg-white shadow-2xl p-6 sm:p-8">
          <div className="text-center mb-6">
            <div className="mx-auto h-14 w-14 rounded-2xl bg-blue-50 text-blue-600 flex items-center justify-center mb-4">
              <Building2 className="h-7 w-7" />
            </div>
            <h1 className="font-heading text-xl sm:text-2xl font-extrabold text-slate-900">{tx("title")}</h1>
            <p className="mt-2 text-sm text-slate-500 leading-relaxed">{tx("subtitle")}</p>
          </div>

          {/* Logo (optional) */}
          <div className="flex items-center gap-4 mb-5">
            <div className="h-16 w-16 rounded-2xl border-2 border-dashed border-slate-200 bg-slate-50 flex items-center justify-center overflow-hidden shrink-0">
              {logoId ? <img src={fileUrl(logoId)} alt="logo" className="h-full w-full object-contain" /> : <Building2 className="h-7 w-7 text-slate-300" />}
            </div>
            <div>
              <p className="text-sm font-medium text-slate-700">{tx("logo")}</p>
              <input ref={logoRef} type="file" accept="image/*" className="hidden" onChange={onLogo} data-testid="onboarding-logo-input" />
              <Button variant="outline" size="sm" className="mt-1.5" onClick={() => logoRef.current?.click()} disabled={logoBusy} data-testid="onboarding-logo-btn">
                {logoBusy ? <Loader2 className="h-4 w-4 mx-1 animate-spin" /> : <Upload className="h-4 w-4 mx-1" />} {tx("logoBtn")}
              </Button>
            </div>
          </div>

          <div className="space-y-4">
            <Field label={tx("nameL")} icon={Building2} required={tx("required")}>
              <Input value={name} onChange={(e) => setName(e.target.value)} placeholder={tx("nameP")}
                dir={dir} data-testid="onboarding-name-input" onKeyDown={(e) => e.key === "Enter" && submit()} />
            </Field>
            <Field label={tx("phoneL")} icon={Phone} required={tx("required")}>
              <Input value={phone} onChange={(e) => setPhone(e.target.value)} placeholder={tx("phoneP")}
                dir="ltr" data-testid="onboarding-phone-input" onKeyDown={(e) => e.key === "Enter" && submit()} />
            </Field>
            <Field label={tx("crL")} icon={FileText} required={tx("required")}>
              <Input value={cr} onChange={(e) => setCr(e.target.value)} placeholder={tx("crP")}
                dir="ltr" data-testid="onboarding-cr-input" onKeyDown={(e) => e.key === "Enter" && submit()} />
            </Field>
          </div>

          <Button onClick={submit} disabled={saving} data-testid="onboarding-submit-btn"
            className="mt-6 w-full h-12 gap-2 bg-blue-600 hover:bg-blue-700 text-base font-semibold">
            {saving ? <Loader2 className="h-5 w-5 animate-spin" /> : <CheckCircle2 className="h-5 w-5" />}
            {tx("submit")}
            {!saving && <ArrowLeft className={`h-4 w-4 ${dir === "rtl" ? "" : "rotate-180"}`} />}
          </Button>
        </motion.div>

        <div className="mt-6 text-center">
          <button data-testid="onboarding-logout-btn" onClick={logout}
            className="inline-flex items-center gap-1.5 text-sm text-slate-400 hover:text-white transition">
            <LogOut className="h-4 w-4" /> {tx("logout")}
          </button>
        </div>
      </div>
    </div>
  );
}

const Field = ({ label, icon: Icon, required, children }) => (
  <div>
    <label className="text-sm font-medium text-slate-700 flex items-center gap-1.5 mb-1.5">
      <Icon className="h-4 w-4 text-blue-500" /> {label}
      <span className="text-[10px] font-bold text-rose-500 bg-rose-50 rounded px-1.5 py-0.5">{required}</span>
    </label>
    {children}
  </div>
);
